import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { currentPassword: "", newPassword: "", confirmPassword: "" };

function strength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const LABELS = ["", "Weak", "Fair", "Good", "Strong", "Excellent"];

export default function AdminSecurity() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.currentPassword) return "Enter your current password";
    if (form.newPassword.length < 8) return "New password must be at least 8 characters";
    if (!/[A-Za-z]/.test(form.newPassword) || !/\d/.test(form.newPassword)) return "New password must include a letter and a number";
    if (form.newPassword !== form.confirmPassword) return "Passwords do not match";
    if (form.newPassword === form.currentPassword) return "New password must be different from the current one";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success("Password updated. Use your new password next time you log in.");
      setForm(EMPTY);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const s = strength(form.newPassword);
  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-3 pr-11 text-sm outline-none focus:border-terracotta";
  const type = show ? "text" : "password";

  return (
    <div data-testid="admin-security-page" className="max-w-xl">
      <h1 className="font-serif text-3xl text-charcoal">Security</h1>
      <p className="mt-1 text-sm text-taupe">Change the password for <span className="font-medium text-charcoal">{user?.email}</span>.</p>

      <form onSubmit={submit} className="mt-8 space-y-5 rounded-3xl border border-soft-border bg-cream p-6 md:p-8">
        <div className="flex items-center gap-3 rounded-2xl bg-blush/60 p-4 text-sm text-taupe">
          <ShieldCheck className="h-5 w-5 shrink-0 text-terracotta" />
          Use at least 8 characters with a mix of letters and numbers. Avoid reusing passwords from other sites.
        </div>

        {[
          { k: "currentPassword", label: "Current Password" },
          { k: "newPassword", label: "New Password" },
          { k: "confirmPassword", label: "Confirm New Password" },
        ].map((f) => (
          <div key={f.k}>
            <label className="mb-1.5 block text-sm font-medium text-charcoal">{f.label}</label>
            <div className="relative">
              <input data-testid={`security-${f.k}`} type={type} value={form[f.k]} onChange={set(f.k)} autoComplete={f.k === "currentPassword" ? "current-password" : "new-password"} className={inputCls} />
              <button type="button" aria-label={show ? "Hide passwords" : "Show passwords"} onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-taupe hover:text-terracotta">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        ))}

        {form.newPassword && (
          <div data-testid="security-strength">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= s ? (s <= 2 ? "bg-red-400" : s <= 3 ? "bg-gold" : "bg-emerald-500") : "bg-soft-border"}`} />)}
            </div>
            <p className="mt-1.5 text-xs text-taupe">Strength: {LABELS[s]}</p>
          </div>
        )}

        <button data-testid="security-save" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-cream hover:bg-terracotta-dark disabled:opacity-70">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update Password
        </button>
      </form>
    </div>
  );
}
